import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Headphones } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SupportContactDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();

  const { data: contact, isLoading } = useQuery({
    queryKey: ['support-contact'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_contacts')
        .select('email, phone')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-[#0880AE]/10 flex items-center justify-center">
              <Headphones className="text-[#0880AE]" size={24} />
            </div>
            <div>
              <DialogTitle className="font-['Lexend_Deca'] text-[#37699E]">
                {t('support.title')}
              </DialogTitle>
              <DialogDescription>{t('support.description')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <a
            href={contact?.email ? `mailto:${contact.email}` : '#'}
            className="flex items-center gap-3 p-4 rounded-lg border border-[#DBE2EA] hover:border-[#0880AE] hover:bg-[#EBF4F8] transition-colors"
          >
            <Mail className="text-[#0880AE] flex-shrink-0" size={20} />
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('support.email')}
              </span>
              <span className="text-sm font-medium text-[#2D3748] truncate">
                {isLoading ? '...' : contact?.email || '-'}
              </span>
            </div>
          </a>

          <a
            href={contact?.phone ? `tel:${contact.phone.replace(/\s/g, '')}` : '#'}
            className="flex items-center gap-3 p-4 rounded-lg border border-[#DBE2EA] hover:border-[#0880AE] hover:bg-[#EBF4F8] transition-colors"
          >
            <Phone className="text-[#0880AE] flex-shrink-0" size={20} />
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('support.phone')}
              </span>
              <span className="text-sm font-medium text-[#2D3748] truncate">
                {isLoading ? '...' : contact?.phone || '-'}
              </span>
            </div>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};
